import type { ApiError } from '../../api/errors';

/**
 * The class tabs, spelled as gdsgate takes them for `--class`.
 *
 * Five of its eight, and the tab that covers the rest is `all` rather than an
 * "other" bucket: a class this console has not heard of must not disappear
 * behind a label that claims to name it.
 *
 * **`authorization` is decisions on a cluster of today, and more than that
 * further back on the same chain.** gdsgate split the class: the MCP descriptor
 * rows became `ResourceIntegrity`, and the refusal for a resource nothing
 * routes to — which no policy was ever asked about — became `SessionActivity`.
 * Rows sealed before that keep the class they were sealed with, because the
 * class is in the row's digest and rewriting it would break the chain. So no
 * tab word is right about both epochs, and the word here names what the class
 * means now. What tells the older rows apart is the event code, which never
 * moved and which the panel shows on the row.
 */
export const CLASSES = [
  'all',
  'admin-action',
  'authorization',
  'authentication',
  'session-activity',
  'resource-integrity',
] as const;

export type ClassFilter = (typeof CLASSES)[number];

/**
 * The gdsgate a class first appeared in, for the classes not every tenancy is
 * new enough to know.
 *
 * A class every supported gdsgate has is **absent**, and the absence is what
 * keeps the sentence off refusals it would not explain. One entry today.
 */
const ARRIVED_IN: Partial<Record<ClassFilter, string>> = {
  'resource-integrity': '0.8.23',
};

/**
 * What to say beside a refused read about the version, or `undefined` when the
 * version has nothing to do with it.
 *
 * **The tab is not hidden on an older tenancy, and this stands in for hiding
 * it.** This console serves tenancies of several versions at once and cannot
 * see which is which before it asks; a tab that appeared for some tenants and
 * not for others would be a console keeping a filter from the people whose
 * cluster is the reason it exists. So the tab is always there, the read is
 * always sent, and a cluster too old to know the word refuses it — `--class`
 * is a command line argument, so its parser turns down the whole invocation
 * rather than answering with no rows.
 *
 * **Two codes come back for that, and they earn two different sentences.**
 * `audit_class_unknown` is the control plane having recognised the argument
 * parser's own refusal of the value: nothing was read, and what stands in the
 * way is the build the tenancy is on. That can be stated. `cluster_refused` is
 * everything else the same read can end in — a tenancy whose policy does not
 * let this console at its chain, and a parser whose wording the control plane
 * has not been taught — and there the version is a possibility rather than the
 * answer, so the words hand over the one test a person can run themselves: the
 * other tabs.
 *
 * The sentence is chosen here and only here, so the two can never be on screen
 * contradicting each other: one refusal carries one code, and one code picks
 * one key.
 */
export interface VersionBehind {
  /** The gdsgate the class arrived in — the version to hold the tenancy against. */
  version: string;
  /** The words for it: a statement where the cluster named the word, a supposition otherwise. */
  key: 'audit.filter.tooNew' | 'audit.filter.tooNew.maybe';
}

export function versionBehindRefusal(
  klass: ClassFilter,
  error: ApiError,
): VersionBehind | undefined {
  const version = ARRIVED_IN[klass];
  if (version === undefined) return undefined;
  if (error.code === 'audit_class_unknown') return { version, key: 'audit.filter.tooNew' };
  if (error.code === 'cluster_refused') return { version, key: 'audit.filter.tooNew.maybe' };
  return undefined;
}
